import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const INFO_LINKS = [
  { path: '/about', label: 'About' },
  { path: '/privacy', label: 'Privacy' },
  { path: '/terms', label: 'Terms' },
];

function InfoHeaderNav({ pathname }) {
  return (
    <nav
      className="flex flex-wrap items-center justify-center gap-2"
      aria-label="Information pages"
    >
      {INFO_LINKS.map((link) => {
        const isActive = pathname === link.path;
        return (
          <Link
            key={link.path}
            to={link.path}
            className={`px-3 md:px-4 py-1.5 md:py-2 rounded-full text-xs md:text-sm font-medium border transition-all duration-200 ${
              isActive
                ? 'bg-accent-blue/20 border-accent-blue/30 text-accent-blue'
                : 'bg-dark-card/40 border-dark-border/80 text-white/55 hover:text-white hover:border-white/20'
            }`}
          >
            {link.label}
          </Link>
        );
      })}
    </nav>
  );
}

function BackToStoreLink() {
  return (
    <Link
      to="/"
      className="px-3 md:px-4 py-1.5 md:py-2 rounded-full text-xs md:text-sm font-medium border border-dark-border/80 bg-dark-card/40 text-white/55 hover:text-white hover:border-white/20 transition-all duration-200 flex-shrink-0"
    >
      Back to store
    </Link>
  );
}

function Header({ onMenuClick }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated } = useAuth();
  const [isScrolled, setIsScrolled] = useState(false);
  const isCreator = user?.role === 'creator' && user?.creatorId != null;
  const isAdmin = user?.role === 'admin';
  const isInfoPage = INFO_LINKS.some((link) => link.path === location.pathname);

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      setIsScrolled(scrollPosition > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <header className={`fixed top-4 left-0 right-0 z-50 border border-dark-border rounded-3xl mx-2 md:mx-5 px-4 md:px-8 py-3 md:py-4 shadow-2xl transition-all duration-300 ${
      isScrolled 
        ? 'bg-gradient-to-br from-dark-surface/80 via-dark-card/80 to-dark-border/80 backdrop-blur-md border-dark-border/60' 
        : 'bg-gradient-to-br from-dark-surface via-dark-card to-dark-border backdrop-blur-sm'
    }`}>
      <div className="flex flex-col gap-3">
      <div className="flex justify-between items-center gap-3">
        <div className="flex items-center gap-3 flex-shrink-0">
          {onMenuClick && (
            <button
              onClick={onMenuClick}
              className="md:hidden p-2 hover:bg-dark-card/50 rounded-lg transition-all duration-200"
              aria-label="Menu"
            >
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
          )}
          <button 
            onClick={() => {
              window.scrollTo(0, 0);
              if (document.documentElement) document.documentElement.scrollTop = 0;
              if (document.body) document.body.scrollTop = 0;
              navigate('/');
            }}
            className="flex items-center gap-2 md:gap-4 hover:opacity-80 transition-opacity duration-200"
          >
            <div className="flex items-center justify-center">
              <img src="/images/logo_white.png" alt="Nightfall Logo" className="h-7 md:h-8 w-auto" />
            </div>
            <h1 className="hidden md:block font-normal text-2xl text-white tracking-tight border-l-2 border-white pl-4">Nightfall</h1>
          </button>
        </div>
        {isInfoPage ? (
          <>
            <div className="hidden lg:flex flex-1 justify-center px-4">
              <InfoHeaderNav pathname={location.pathname} />
            </div>
            <BackToStoreLink />
          </>
        ) : (
        <div className="flex items-center gap-2 md:gap-4 flex-shrink-0">
          {isAuthenticated ? (
            <>
              {isAdmin && (
                <button
                  type="button"
                  onClick={() => {
                    window.scrollTo(0, 0);
                    navigate('/admin/dashboard');
                  }}
                  className="p-2 md:p-2.5 bg-dark-card/60 backdrop-blur-sm border border-dark-border rounded-full hover:bg-dark-card hover:border-accent-blue/30 transition-all duration-200"
                  aria-label="Admin dashboard"
                >
                  <svg
                    className="w-5 h-5 md:w-6 md:h-6 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                    />
                  </svg>
                </button>
              )}
              {isCreator && (
                <button
                  type="button"
                  onClick={() => {
                    window.scrollTo(0, 0);
                    navigate('/creator/dashboard');
                  }}
                  className="p-2 md:p-2.5 bg-dark-card/60 backdrop-blur-sm border border-dark-border rounded-full hover:bg-dark-card hover:border-accent-blue/30 transition-all duration-200"
                  aria-label="Creator dashboard"
                >
                  <svg
                    className="w-5 h-5 md:w-6 md:h-6 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                    />
                  </svg>
                </button>
              )}
              <button
                onClick={() => {
                  window.scrollTo(0, 0);
                  navigate('/search');
                }}
                className="p-2 md:p-2.5 bg-dark-card/60 backdrop-blur-sm border border-dark-border rounded-full hover:bg-dark-card hover:border-accent-blue/30 transition-all duration-200"
                aria-label="Search"
              >
                <svg className="w-5 h-5 md:w-6 md:h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </button>
              {/* Shard balance hidden while store uses USD checkout
              <div className="flex items-center gap-2 bg-dark-card/60 backdrop-blur-sm border border-dark-border rounded-full px-3 md:px-5 py-2 md:py-2.5">
                ...
              </div>
              */}
                    <button
                      onClick={() => {
                        window.scrollTo(0, 0);
                        navigate('/account');
                      }}
                      className="p-2 md:p-2.5 bg-dark-card/60 backdrop-blur-sm border border-dark-border rounded-full hover:bg-dark-card hover:border-accent-blue/30 transition-all duration-200"
                      aria-label="Account"
                    >
                <svg className="w-5 h-5 md:w-6 md:h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </button>
            </>
          ) : (
            <>
              <button
                onClick={() => {
                  window.scrollTo(0, 0);
                  navigate('/search');
                }}
                className="p-2 md:p-2.5 bg-dark-card/60 backdrop-blur-sm border border-dark-border rounded-full hover:bg-dark-card hover:border-accent-blue/30 transition-all duration-200"
                aria-label="Search"
              >
                <svg className="w-5 h-5 md:w-6 md:h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </button>
              <button
                onClick={() => navigate('/signin')}
                className="px-4 md:px-6 py-2 md:py-2.5 bg-accent-blue/20 border border-accent-blue/30 rounded-full text-accent-blue font-medium hover:bg-accent-blue/30 hover:border-accent-blue/40 transition-all duration-200 text-sm md:text-base"
              >
                Sign In
              </button>
            </>
          )}
        </div>
        )}
      </div>
      {isInfoPage && (
        <div className="lg:hidden border-t border-dark-border/60 pt-3">
          <InfoHeaderNav pathname={location.pathname} />
        </div>
      )}
      </div>
    </header>
  );
}

export default Header;
